import React, { useState } from 'react';
import { addActivity, updateActivity, removeActivity } from '../utils/api';

const QUICK_PICKS = ['Meditation', 'Forgiveness', 'Pranic Breathing', 'Salt Water Treatment', 'Affirmations', 'Twin Hearts Meditation', 'Other'];

const SLOT_LABELS = { 1: ['Daily'], 2: ['Morning', 'Evening'], 3: ['Morning', 'Afternoon', 'Evening'] };

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Format YYYY-MM-DD from a Date
function toDateStr(d) {
  var dt = new Date(d);
  return dt.getFullYear() + '-' + String(dt.getMonth() + 1).padStart(2, '0') + '-' + String(dt.getDate()).padStart(2, '0');
}

// Given an activity, compute expected scheduled days from startDate up to today (or endDate)
function getScheduledDates(act) {
  var start = new Date(act.startDate);
  start.setHours(0, 0, 0, 0);
  var end = act.endDate ? new Date(act.endDate) : new Date();
  end.setHours(23, 59, 59, 999);
  var today = new Date();
  if (end > today) end = today;

  var dates = [];
  var cur = new Date(start);
  while (cur <= end) {
    var dow = cur.getDay(); // 0-6
    var scheduled = act.frequency === 'daily' || act.daysOfWeek.length === 0 || act.daysOfWeek.includes(dow);
    if (scheduled) dates.push(toDateStr(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

// Compliance % for an activity
function compliance(act) {
  var scheduled = getScheduledDates(act);
  if (scheduled.length === 0) return 100;
  var expected = scheduled.length * act.timesPerDay;
  var done = act.checkIns ? act.checkIns.filter(function(c) { return scheduled.includes(c.date); }).length : 0;
  return Math.round((done / expected) * 100);
}

function countCheckedSlots(checkIns, dateStr) {
  if (!checkIns) return 0;
  var count = 0;
  for (var j = 0; j < checkIns.length; j++) {
    if (checkIns[j].date === dateStr) count++;
  }
  return count;
}

// Last 7 days strip for an activity
function last7(act) {
  var days = [];
  for (let i = 6; i >= 0; i--) {
    let d = new Date();
    d.setDate(d.getDate() - i);
    d.setHours(0, 0, 0, 0);
    let ds = toDateStr(d);
    let dow = d.getDay();
    let scheduled = act.frequency === 'daily' || act.daysOfWeek.length === 0 || act.daysOfWeek.includes(dow);
    if (!scheduled) { days.push({ date: ds, state: 'off' }); continue; }
    if (new Date(act.startDate) > d) { days.push({ date: ds, state: 'off' }); continue; }
    let checkedSlots = countCheckedSlots(act.checkIns, ds);
    if (checkedSlots >= act.timesPerDay) days.push({ date: ds, state: 'done' });
    else if (checkedSlots > 0) days.push({ date: ds, state: 'partial' });
    else {
      let today = new Date(); today.setHours(0,0,0,0);
      days.push({ date: ds, state: d < today ? 'missed' : 'today' });
    }
  }
  return days;
}

const ActivityPanel = ({ requestId, activities = [], onUpdate, readonly = false }) => {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', frequency: 'daily', daysOfWeek: [], timesPerDay: 1, startDate: toDateStr(new Date()), endDate: '' });

  const active = activities.filter(function(a) { return a.isActive; });
  const inactive = activities.filter(function(a) { return !a.isActive; });

  function resetForm() {
    setForm({ name: '', description: '', frequency: 'daily', daysOfWeek: [], timesPerDay: 1, startDate: toDateStr(new Date()), endDate: '' });
    setEditId(null);
    setShowForm(false);
  }

  async function handleSave() {
    if (!form.name.trim() || !form.startDate) return;
    setSaving(true);
    try {
      var payload = { ...form, daysOfWeek: form.daysOfWeek.map(Number) };
      var res;
      if (editId) {
        res = await updateActivity(requestId, editId, payload);
      } else {
        res = await addActivity(requestId, payload);
      }
      onUpdate(res.data.activities);
      resetForm();
    } catch (e) { alert('Failed to save activity'); }
    setSaving(false);
  }

  async function handleRemove(actId) {
    if (!window.confirm('Remove this activity? Check-in history is preserved.')) return;
    try {
      var res = await removeActivity(requestId, actId);
      onUpdate(res.data.activities);
    } catch (e) { alert('Failed to remove'); }
  }

  function startEdit(act) {
    setForm({
      name: act.name,
      description: act.description || '',
      frequency: act.frequency,
      daysOfWeek: act.daysOfWeek || [],
      timesPerDay: act.timesPerDay,
      startDate: toDateStr(act.startDate),
      endDate: act.endDate ? toDateStr(act.endDate) : ''
    });
    setEditId(act._id);
    setShowForm(true);
  }

  const pct = compliance;

  const s = {
    panel: { marginTop: '12px', background: '#f9faf7', borderRadius: '8px', padding: '16px', border: '1px solid #e8f0e8' },
    actRow: { background: '#fff', border: '1px solid #e8f0e8', borderRadius: '8px', padding: '14px', marginBottom: '10px' },
    actName: { fontFamily: "'Raleway', sans-serif", fontWeight: '700', fontSize: '14px', color: '#1a1a2e', marginBottom: '2px' },
    actDesc: { fontSize: '12px', color: '#666', marginBottom: '10px', lineHeight: '1.5' },
    actMeta: { fontSize: '11px', color: '#999', marginBottom: '8px' },
    stripWrap: { display: 'flex', gap: '4px', marginBottom: '10px' },
    dot: { width: '28px', height: '28px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: '700', color: '#fff', flexDirection: 'column', gap: '1px' },
    progressBar: { height: '6px', borderRadius: '3px', background: '#e8f0e8', overflow: 'hidden', marginBottom: '4px' },
    progressFill: function(p) { return { height: '100%', width: p + '%', background: p >= 70 ? '#27ae60' : p >= 40 ? '#f39c12' : '#e74c3c', borderRadius: '3px', transition: 'width 0.5s ease' }; },
    pctLabel: { fontSize: '11px', color: '#666' },
    actBtns: { display: 'flex', gap: '8px', marginTop: '10px' },
    smBtn: { background: 'transparent', border: '1px solid #ccc', color: '#666', borderRadius: '4px', padding: '5px 12px', fontSize: '11px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px' },
    addBtn: { background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 18px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer', marginBottom: '14px' },
    formBox: { background: '#fff', border: '1px dashed #4a8f82', borderRadius: '8px', padding: '16px', marginBottom: '14px' },
    label: { display: 'block', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', color: '#666', fontFamily: "'Raleway', sans-serif", marginBottom: '5px', marginTop: '12px' },
    input: { width: '100%', padding: '9px 12px', border: '1px solid #e0e0e0', borderRadius: '5px', fontSize: '13px', fontFamily: "'Open Sans', sans-serif", boxSizing: 'border-box' },
    dayBtns: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' },
    dayBtn: function(on) { return { padding: '5px 10px', borderRadius: '4px', border: '1px solid ' + (on ? '#4a8f82' : '#ccc'), background: on ? '#4a8f82' : '#fff', color: on ? '#fff' : '#666', fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }; },
    formBtns: { display: 'flex', gap: '8px', marginTop: '14px' },
    saveBtn: { flex: 1, background: '#4a8f82', color: '#fff', border: 'none', borderRadius: '4px', padding: '10px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
    cancelBtn: { flex: 1, background: 'transparent', color: '#666', border: '1px solid #ccc', borderRadius: '4px', padding: '10px', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
    quickPicks: { display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' },
    qp: { padding: '4px 10px', border: '1px solid #c9e4de', borderRadius: '20px', background: '#f0faf8', color: '#4a8f82', fontSize: '11px', cursor: 'pointer', fontFamily: "'Raleway', sans-serif", fontWeight: '700' },
    emptyText: { fontSize: '13px', color: '#999', textAlign: 'center', padding: '12px 0' },
    dotColor: { done: '#27ae60', partial: '#f39c12', missed: '#ffcdd2', today: '#e8f0e8', off: '#f5f5f5' },
    dotTextColor: { done: '#fff', partial: '#fff', missed: '#e74c3c', today: '#999', off: '#ccc' }
  };

  function toggleDay(d) {
    var cur = form.daysOfWeek.includes(d) ? form.daysOfWeek.filter(function(x) { return x !== d; }) : [...form.daysOfWeek, d];
    setForm({ ...form, daysOfWeek: cur, frequency: cur.length > 0 ? 'custom' : 'daily' });
  }

  return (
    <div style={s.panel}>

      {/* Add Activity button */}
      {!readonly && (
        <button onClick={function() { resetForm(); setShowForm(true); }} style={s.addBtn}>+ Assign Activity</button>
      )}

      {/* Add / Edit Form */}
      {showForm && !readonly && (
        <div style={s.formBox}>
          <div style={{ fontFamily: "'Raleway', sans-serif", fontWeight: '700', fontSize: '13px', color: '#1a1a2e', marginBottom: '8px' }}>
            {editId ? 'Edit Activity' : 'Assign New Activity'}
          </div>

          {/* Quick picks */}
          {!editId && (
            <>
              <label style={s.label}>Quick Pick</label>
              <div style={s.quickPicks}>
                {QUICK_PICKS.map(function(q) {
                  return (
                    <span key={q} style={s.qp} onClick={function() { setForm({ ...form, name: q === 'Other' ? '' : q }); }}>
                      {q}
                    </span>
                  );
                })}
              </div>
            </>
          )}

          <label style={s.label}>Activity Name *</label>
          <input style={s.input} value={form.name} onChange={function(e) { setForm({ ...form, name: e.target.value }); }} placeholder="e.g. Morning Meditation" />

          <label style={s.label}>Instructions for patient</label>
          <textarea style={{ ...s.input, height: '70px', resize: 'vertical' }} value={form.description} onChange={function(e) { setForm({ ...form, description: e.target.value }); }} placeholder="Describe what the patient should do..." />

          <label style={s.label}>Times per day</label>
          <select style={s.input} value={form.timesPerDay} onChange={function(e) { setForm({ ...form, timesPerDay: parseInt(e.target.value) }); }}>
            <option value={1}>Once (Daily)</option>
            <option value={2}>Twice (Morning + Evening)</option>
            <option value={3}>Three times (Morning, Afternoon, Evening)</option>
          </select>

          <label style={s.label}>Specific days only? (leave all off = every day)</label>
          <div style={s.dayBtns}>
            {DAY_NAMES.map(function(d, i) {
              return (
                <button type="button" key={d} onClick={function() { toggleDay(i); }}
                  style={s.dayBtn(form.daysOfWeek.includes(i))}>
                  {d}
                </button>
              );
            })}
          </div>
          {form.daysOfWeek.length > 0 && (
            <div style={{ fontSize: '11px', color: '#4a8f82', marginTop: '4px' }}>
              Scheduled: {form.daysOfWeek.sort().map(function(d) { return DAY_NAMES[d]; }).join(', ')}
            </div>
          )}

          <label style={s.label}>Start date *</label>
          <input type="date" style={s.input} value={form.startDate} onChange={function(e) { setForm({ ...form, startDate: e.target.value }); }} />

          <label style={s.label}>End date (optional — leave blank = indefinite)</label>
          <input type="date" style={s.input} value={form.endDate} onChange={function(e) { setForm({ ...form, endDate: e.target.value }); }} />

          <div style={s.formBtns}>
            <button onClick={handleSave} disabled={saving} style={s.saveBtn}>{saving ? 'Saving...' : editId ? 'Update' : 'Assign'}</button>
            <button onClick={resetForm} style={s.cancelBtn}>Cancel</button>
          </div>
        </div>
      )}

      {/* Active activities */}
      {active.length === 0 && !showForm && (
        <div style={s.emptyText}>No activities assigned yet.</div>
      )}

      {active.map(function(act) {
        var p = pct(act);
        var strip = last7(act);
        var slots = SLOT_LABELS[act.timesPerDay] || ['Daily'];
        return (
          <div key={act._id} style={s.actRow}>
            <div style={s.actName}>{act.name}</div>
            {act.description && <div style={s.actDesc}>{act.description}</div>}
            <div style={s.actMeta}>
              {slots.join(' · ')} &nbsp;·&nbsp;
              {act.frequency === 'daily' || act.daysOfWeek.length === 0 ? 'Every day' : act.daysOfWeek.sort().map(function(d) { return DAY_NAMES[d]; }).join(', ')}
              &nbsp;·&nbsp; From {new Date(act.startDate).toLocaleDateString('en-IN')}
              {act.endDate && ' to ' + new Date(act.endDate).toLocaleDateString('en-IN')}
              &nbsp;·&nbsp; Assigned by {act.assignedByName}
            </div>

            {/* 7-day strip */}
            <div style={s.stripWrap}>
              {strip.map(function(day) {
                var d = new Date(day.date);
                return (
                  <div key={day.date} title={day.date}
                    style={{ ...s.dot, background: s.dotColor[day.state] || '#f5f5f5', color: s.dotTextColor[day.state] || '#ccc' }}>
                    <span>{DAY_NAMES[d.getDay()]}</span>
                    <span>{day.state === 'done' ? '✓' : day.state === 'partial' ? '~' : day.state === 'missed' ? '✗' : '·'}</span>
                  </div>
                );
              })}
            </div>

            {/* Progress bar */}
            <div style={s.progressBar}>
              <div style={s.progressFill(p)} />
            </div>
            <div style={s.pctLabel}>
              {p}% compliance &nbsp;·&nbsp; {act.checkIns ? act.checkIns.length : 0} total check-ins
            </div>

            {/* Edit / Remove buttons */}
            {!readonly && (
              <div style={s.actBtns}>
                <button onClick={function() { startEdit(act); }} style={s.smBtn}>Edit</button>
                <button onClick={function() { handleRemove(act._id); }} style={{ ...s.smBtn, color: '#e74c3c', borderColor: '#e74c3c' }}>Remove</button>
              </div>
            )}
          </div>
        );
      })}

      {/* Inactive / removed activities (collapsed) */}
      {inactive.length > 0 && (
        <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
          {inactive.length} removed activit{inactive.length > 1 ? 'ies' : 'y'} (history preserved)
        </div>
      )}
    </div>
  );
};

export default ActivityPanel;
